import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { msalInstance } from '../config/auth';
import { environment } from '../config/environment';
import { activityTracker } from './activity-tracker.service';

/**
 * HTTP client service for API communication with MSAL authentication
 * Handles token acquisition (MSAL manages refresh automatically), and error handling
 * Supports both JSON and blob responses
 *
 * Use cases:
 * - All API requests throughout the application
 * - Automatic Bearer token injection for authenticated requests
 * - 401/403 error handling with automatic logout/role update
 * - File downloads with proper filename extraction
 */
class ApiService {
  private api: AxiosInstance;

  constructor() {
    // Create axios instance without baseURL initially
    this.api = axios.create({
      timeout: environment.backendTimeout
    });

    this.setupInterceptors();
  }

  /**
   * Gets the base API URL from environment configuration
   * @private
   * @returns {string} The base API URL
   */
  private getBaseURL(): string {
    return environment.apiUrl;
  }

  /**
   * Sets up request and response interceptors for authentication and error handling
   * Request interceptor: Acquires token via MSAL (which handles refresh automatically)
   * Response interceptor: Handles 401 (logout) and 403 (role update check) errors
   * @private
   */
  private setupInterceptors() {
    // Request interceptor - acquires token silently before each request
    this.api.interceptors.request.use(async config => {
      // Skip auth in dev mode
      if (environment.devAuthOff) {
        return config;
      }

      // Only add auth header for API calls to the IAS service
      if (config.url && (config.url.startsWith('/') || config.url.startsWith(environment.apiUrl))) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const account = accounts[0];
          const request = {
            scopes: environment.azure.scopes,
            account: account
          };

          try {
            // MSAL handles token refresh automatically via acquireTokenSilent
            const response = await msalInstance.acquireTokenSilent(request);
            config.headers.Authorization = `Bearer ${response.accessToken}`;
            activityTracker.recordActivity();
          } catch (tokenError) {
            const { authService } = await import('./auth.service');
            authService.logout();
            return Promise.reject(tokenError);
          }
        }
      }
      return config;
    });

    this.api.interceptors.response.use(
      response => response,
      async error => {
        // Skip auth error handling in dev mode
        if (environment.devAuthOff) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401) {
          import('./auth.service').then(({ authService }) => {
            authService.logout();
          });
        } else if (error.response?.status === 403) {
          const { authService } = await import('./auth.service');
          await authService.checkRoleUpdate();
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Extracts data from API response, handling both new standardized format and legacy format
   * @private
   * @template T
   * @param {any} response - API response object
   * @returns {T} Extracted data
   */
  private extractApiData<T>(response: any): T {
    if (response && typeof response === 'object' && 'data' in response) {
      return response.data;
    }
    return response;
  }

  /**
   * Performs GET request
   * @template T
   * @param {string} url - Endpoint URL (relative or absolute)
   * @param {AxiosRequestConfig} [config] - Optional axios config
   * @returns {Promise<T>} Response data
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const fullUrl = url.startsWith('http') ? url : `${this.getBaseURL()}${url}`;
    const response = await this.api.get(fullUrl, config);
    return this.extractApiData<T>(response.data);
  }

  /**
   * Performs POST request
   * @template T
   * @param {string} url - Endpoint URL (relative or absolute)
   * @param {any} [data] - Request body data
   * @param {AxiosRequestConfig} [config] - Optional axios config
   * @returns {Promise<T>} Response data
   */
  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const fullUrl = url.startsWith('http') ? url : `${this.getBaseURL()}${url}`;
    const response = await this.api.post(fullUrl, data, config);
    return this.extractApiData<T>(response.data);
  }

  /**
   * Performs PUT request
   * @template T
   * @param {string} url - Endpoint URL (relative or absolute)
   * @param {any} [data] - Request body data
   * @param {AxiosRequestConfig} [config] - Optional axios config
   * @returns {Promise<T>} Response data
   */
  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const fullUrl = url.startsWith('http') ? url : `${this.getBaseURL()}${url}`;
    const response = await this.api.put(fullUrl, data, config);
    return this.extractApiData<T>(response.data);
  }

  /**
   * Performs DELETE request
   * @template T
   * @param {string} url - Endpoint URL (relative or absolute)
   * @param {AxiosRequestConfig} [config] - Optional axios config
   * @returns {Promise<T>} Response data
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const fullUrl = url.startsWith('http') ? url : `${this.getBaseURL()}${url}`;
    const response = await this.api.delete(fullUrl, config);
    return this.extractApiData<T>(response.data);
  }

  /**
   * Performs GET request for blob response (file downloads)
   * Extracts filename from Content-Disposition header
   * Handles error responses that come as JSON instead of blob
   * @param {string} url - Endpoint URL (relative or absolute)
   * @param {AxiosRequestConfig} [config] - Optional axios config
   * @returns {Promise<{blob: Blob, filename: string}>} Blob data and extracted filename
   * @throws {Error} Throws error with message from server response if download fails
   */
  async getBlob(url: string, config?: AxiosRequestConfig): Promise<{ blob: Blob; filename: string }> {
    const fullUrl = url.startsWith('http') ? url : `${this.getBaseURL()}${url}`;
    
    try {
      const response = await this.api.get(fullUrl, { ...config, responseType: 'blob' });

      // Check if the response is actually an error disguised as a blob
      const contentType = response.headers['content-type'];
      
      if (contentType && contentType.includes('application/json')) {
        // Convert blob back to text to read the error
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || 'Помилка завантаження файлу');
      }

      const contentDisposition = response.headers['content-disposition'];
      let filename = 'download.xlsx';

      if (contentDisposition) {
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
        if (utf8Match && utf8Match[1]) {
          filename = decodeURIComponent(utf8Match[1]);
        } else {
          const standardMatch = contentDisposition.match(/filename[^;=\n]*=((['\"]).*?\2|[^;\n]*)/i);
          if (standardMatch && standardMatch[1]) {
            filename = standardMatch[1].replace(/[\"']/g, '');
          }
        }
      }

      return { blob: response.data, filename };
    } catch (error: any) {
      // Handle axios errors with response data
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          if (errorData.error) {
            throw new Error(errorData.error);
          }
        } catch (parseError) {
          // If the parseError is actually our intended error message, re-throw it
          if (parseError instanceof Error && parseError.message !== 'Unexpected token' && !parseError.message.includes('JSON')) {
            throw parseError;
          }
          // Otherwise, it's a real parsing error, fall through to generic message
        }
      }
      
      // If we couldn't extract the backend error, provide a generic message
      throw new Error('Помилка завантаження файлу');
    }
  }
}

export const apiService = new ApiService();
export const apiClient = apiService;

/**
 * Utility function to download blob with filename
 * @param {Blob} blob - Blob data to download
 * @param {string} filename - Filename for the downloaded file
 */
export const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};
